import * as THREE from 'three/webgpu';

/** 목적 회전의 연속된 호를 추적해 180도 경계에서 혼합 방향이 뒤집히지 않게 한다. */
export class PoseTransition {
  private readonly poses;
  private readonly relative = new THREE.Quaternion();
  private readonly rotation = new THREE.Quaternion();
  private elapsed = 0;
  private duration = 0;
  private power = 1;
  private initialize = false;
  private applied = false;
  active = false;

  /** 골격과 검 소켓이 공유하는 출발 자세의 잔여 가중치. 기본 전환은 선형이다. */
  get linearWeight(): number {
    return this.active ? (1 - this.elapsed / this.duration) ** this.power : 0;
  }

  constructor(bones: readonly THREE.Bone[]) {
    this.poses = bones.map(bone => ({
      bone, fromQ:new THREE.Quaternion(), fromP:new THREE.Vector3(),
      capturedQ:new THREE.Quaternion(), capturedP:new THREE.Vector3(),
      inverseFrom:new THREE.Quaternion(), target:new THREE.Quaternion(),
      baseQ:new THREE.Quaternion(), baseP:new THREE.Vector3(),
      axis:new THREE.Vector3(1,0,0), angle:0,
    }));
  }

  /** 접지와 직전 이행 보정을 제거하기 전에 실제로 보이던 자세를 저장한다. */
  capture(): void {
    for (const p of this.poses) {
      p.capturedQ.copy(p.bone.quaternion).normalize();
      p.capturedP.copy(p.bone.position);
    }
  }

  restore(): void {
    if (!this.applied) return;
    for (const p of this.poses) {
      p.bone.quaternion.copy(p.baseQ);
      p.bone.position.copy(p.baseP);
    }
    this.applied = false;
  }

  /** power<1은 초반 이행 속도를 낮춘다. 생략하면 기존 보행·회피 출구의 선형 곡선을 유지한다. */
  begin(duration: number, power = 1): void {
    this.elapsed = 0;
    this.duration = Math.max(0, duration);
    this.power = power;
    this.active = this.duration > 0;
    this.initialize = this.active;
  }

  /** mixer 뒤, 검 소켓과 접지 앞. 첫 표본은 직전 자세에서 시작한다. */
  apply(dt: number, phaseAge?: number): void {
    if (!this.active) return;
    const first = this.initialize;
    // 공격 첫 렌더가 늦어도 판정 시각을 넘어 이행이 남지 않도록 실제 공격 위상을 우선한다.
    if (phaseAge !== undefined) this.elapsed = Math.min(this.duration, Math.max(this.elapsed, phaseAge));
    else if (!first) this.elapsed = Math.min(this.duration, this.elapsed + Math.max(0, dt));
    if (this.elapsed >= this.duration) {
      this.active = false;
      return;
    }
    const u = 1 - (1 - this.elapsed / this.duration) ** this.power;
    for (const p of this.poses) {
      p.baseQ.copy(p.bone.quaternion);
      p.baseP.copy(p.bone.position);
      if (first) { p.fromQ.copy(p.capturedQ); p.fromP.copy(p.capturedP); }
      const sign = (first ? p.fromQ : p.target).dot(p.baseQ) < 0 ? -1 : 1;
      p.target.set(sign*p.baseQ.x, sign*p.baseQ.y, sign*p.baseQ.z, sign*p.baseQ.w);
      if (first) { p.inverseFrom.copy(p.fromQ).invert(); p.angle=0; }
      this.relative.copy(p.inverseFrom).multiply(p.target).normalize();
      const length = Math.hypot(this.relative.x, this.relative.y, this.relative.z);
      let angle = 2*Math.atan2(length, this.relative.w);
      let previousProjection = first ? 0 : p.angle;
      if (length > 1e-8) {
        const dot = this.relative.x*p.axis.x + this.relative.y*p.axis.y + this.relative.z*p.axis.z;
        previousProjection = first ? 0 : p.angle*dot/length;
        p.axis.set(this.relative.x, this.relative.y, this.relative.z).multiplyScalar(1/length);
      }
      // 같은 quaternion의 주기는4π다. 360도를 넘어도 직전 회전의 가지를 보존한다.
      angle += 4*Math.PI*Math.round((previousProjection-angle)/(4*Math.PI));
      p.angle=angle;
      this.rotation.setFromAxisAngle(p.axis, angle*u);
      p.bone.quaternion.copy(p.fromQ).multiply(this.rotation).normalize();
      p.bone.position.copy(p.fromP).lerp(p.baseP,u);
    }
    this.initialize = false;
    this.applied = true;
  }

  reset(): void {
    this.restore();
    this.active = false;
    this.initialize = false;
    this.elapsed = 0;
  }
}
